import { useState, useCallback, useMemo } from 'react';

import { useAuth } from '../context/AuthContext';
import {
  listMedia,
  archiveMedia,
  restoreMedia,
  deleteMedia,
  uploadMediaFile,
} from '../api/media.api';
import type {
  MediaAsset,
  MediaAssetType,
  MediaAssetStatus,
  UploadProgressEvent,
} from '../api/media.api';

export type { MediaAsset, MediaAssetType, MediaAssetStatus };

export type MediaFilter = {
  type: 'all' | MediaAssetType;
  status: 'active' | 'archived' | 'all';
  search: string;
};

export interface UploadingFile {
  id: string;
  name: string;
  sizeBytes: number;
  percent: number;
  error: string | null;
  abortController: AbortController;
}

export function useMediaLibrary() {
  const { user, isSuperAdmin } = useAuth();

  const [assets, setAssets] = useState<MediaAsset[]>(() =>
    user ? listMedia(user.id, isSuperAdmin) : []
  );
  const [uploading, setUploading] = useState<UploadingFile[]>([]);
  const [filter, setFilter] = useState<MediaFilter>({ type: 'all', status: 'active', search: '' });

  const refresh = useCallback(() => {
    if (!user) return;
    setAssets(listMedia(user.id, isSuperAdmin));
  }, [user, isSuperAdmin]);

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (!user) return;

      for (const file of files) {
        const entryId = `${Date.now()}-${file.name}`;
        const abortController = new AbortController();

        setUploading((prev) => [
          ...prev,
          {
            id: entryId,
            name: file.name,
            sizeBytes: file.size,
            percent: 0,
            error: null,
            abortController,
          },
        ]);

        const onProgress = (e: UploadProgressEvent) => {
          setUploading((prev) =>
            prev.map((u) => (u.id === entryId ? { ...u, percent: e.percent } : u))
          );
        };

        try {
          await uploadMediaFile(
            file,
            user.id,
            user.email ?? null,
            onProgress,
            abortController.signal
          );
          setUploading((prev) => prev.filter((u) => u.id !== entryId));
          setAssets(listMedia(user.id, isSuperAdmin));
        } catch (err: unknown) {
          const message =
            err instanceof DOMException && err.name === 'AbortError'
              ? 'Cancelled'
              : ((err as { message?: string }).message ?? 'Upload failed');
          setUploading((prev) =>
            prev.map((u) => (u.id === entryId ? { ...u, error: message } : u))
          );
        }
      }
    },
    [user, isSuperAdmin]
  );

  const cancelUpload = useCallback((id: string) => {
    setUploading((prev) => {
      const entry = prev.find((u) => u.id === id);
      entry?.abortController.abort();
      return prev.filter((u) => u.id !== id);
    });
  }, []);

  const dismissUploadError = useCallback((id: string) => {
    setUploading((prev) => prev.filter((u) => u.id !== id));
  }, []);

  const archive = useCallback(
    (id: string) => {
      if (!user) return;
      archiveMedia(id, user.id, isSuperAdmin);
      setAssets(listMedia(user.id, isSuperAdmin));
    },
    [user, isSuperAdmin]
  );

  const restore = useCallback(
    (id: string) => {
      if (!user) return;
      restoreMedia(id, user.id, isSuperAdmin);
      setAssets(listMedia(user.id, isSuperAdmin));
    },
    [user, isSuperAdmin]
  );

  const remove = useCallback(
    (id: string) => {
      if (!user) return;
      deleteMedia(id, user.id, isSuperAdmin);
      setAssets(listMedia(user.id, isSuperAdmin));
    },
    [user, isSuperAdmin]
  );

  const filteredAssets = useMemo(() => {
    return assets.filter((a) => {
      if (filter.type !== 'all' && a.type !== filter.type) return false;
      if (filter.status === 'active' && a.status === 'archived') return false;
      if (filter.status === 'archived' && a.status !== 'archived') return false;
      if (filter.search) {
        const q = filter.search.toLowerCase();
        if (!a.name.toLowerCase().includes(q) && !(a.altText ?? '').toLowerCase().includes(q))
          return false;
      }
      return true;
    });
  }, [assets, filter]);

  return {
    assets: filteredAssets,
    totalCount: assets.length,
    uploading,
    filter,
    setFilter,
    uploadFiles,
    cancelUpload,
    dismissUploadError,
    archive,
    restore,
    remove,
    refresh,
    isSuperAdmin,
  };
}
